import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import { Colors } from "../../../../constants/colors.constants";
import { Trans, useTranslation } from "react-i18next";
import { useObservable } from "@ngneat/react-rxjs";
import {
  transactionsQuery,
  transactionServices,
  TransactionLight,
  TransactionStatus,
  SwanStatusInfo,
  searchTransactionTotalEffects,
  TransactionPeriod,
  searchTransactionPeriodsEffects,
  TransactionFull,
} from "../../../../store/transaction";
import { useEffectFn } from "@ngneat/effects-hooks";

import BasicSearchbar from "../../../../components/inputs/BasicSearchBar.component";
import Chevron from "../../../../assets/icons/icon_arrow_down_dark.svg";
import FilterCard from "../../../../components/card/FilterCard.component";
import Export from "../../../../assets/icons/icon_export_light.svg";
import TransactionDetail from "./TransactionDetail.modal";
import { enqueueSnackbar } from "notistack";
import ExportTransactions from "./ExportTransactions.modal";
import ClosePeriod from "./ClosePeriod.modal";
import { UserRole } from "../../../../store/users";
import PaymentCardComponent from "../../../../components/card/PaymentCard.component";
import { useResponsive } from "../../../../utils/useResponsive";
import { paymentService } from "../../../../store/payments/payments.services";
import { getAccountBalance } from "src/store/swan/swan.services";
import { useLocation } from "react-router-dom";
import { sessionQuery, sessionService } from "src/store/session";
import SharedCardSection from "./SharedCardSection";

const TRANSACTIONS_PER_PAGE = 20;

const PaymentScreen = (props: { role: UserRole }) => {
  const isMobile: boolean = useResponsive();
  const user = sessionQuery.user;
  const { t } = useTranslation();
  const { role } = props;
  const location = useLocation();

  const [transactions] = useObservable(transactionsQuery.transactions$);
  const [{ transactionTotal }] = useObservable(transactionsQuery.totals$);
  const [filters] = useObservable(transactionsQuery.filters$);
  const [periods] = useObservable(transactionsQuery.periods$);
  const [isLoading] = useObservable(transactionsQuery.loading$);
  const [selectedPeriod] = useObservable(transactionsQuery.selectedPeriod$);
  const [accountBalance, setAccountBalance] = useState<string>();

  const searchPeriods = useEffectFn(searchTransactionPeriodsEffects);
  const searchTransactionsTotal = useEffectFn(searchTransactionTotalEffects);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionFull | TransactionLight | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editmodalOpen, setEditModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [{ company }] = useObservable(sessionQuery.company$);

  const [periodModalOpen, setPeriodModalOpen] = useState(false);

  const [expanded, setExpanded] = React.useState<string | false>(false);

  const [loadingPeriod, setLoadingPeriod] = useState<string | null>(null);

  const loadingRef = useRef(null);

  const handleChange =
    (period: TransactionPeriod) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        setLoadingPeriod(period.id);

        transactionServices.getTransactionsByPeriod(period, filters).subscribe({
          next: () => {
            setLoadingPeriod(null);
          },
          error: (err) => {
            enqueueSnackbar(err.text, err.options);
            setLoadingPeriod(null);
          },
        });
        setExpanded(period.id);
      } else {
        setExpanded(false);
        setLoadingPeriod(null);
      }
    };

  useEffect(() => {
    if (selectedPeriod) {
      transactionServices.getTransactionsByPeriod(selectedPeriod, filters).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [selectedPeriod, filters]);

  useEffect(() => {
    sessionService.getCompany().subscribe();
  }, []);

  useEffect(() => {
    transactionServices.setFilters({ search: location.state?.categoryName, userId: location.state?.userId });
  }, [location]);

  useEffect(() => {
    getAccountBalance().then((x) => {
      if (x) setAccountBalance(x[0].balances.available.value);
    });
  }, []);

  useEffect(() => {
    const paymentId = paymentService.getPaymentIdFromUrl();
    if (paymentId && periods?.length > 0) {
      const subscription = transactionServices
        .getTransactionById(paymentId)
        .subscribe({
          next: (transaction) => {
            if (transaction) {
              setSelectedTransaction(transaction);
              setModalOpen(true);
              setEditModalOpen(true);
              setTimeout(() => {
                setEditModalOpen(false);
              }, 2000);
            }
            paymentService.removePaymentIdFromLocal();
          },
          error: (err) => {
            paymentService.removePaymentIdFromLocal();
          },
        });

      return () => subscription.unsubscribe();
    }
  }, [periods]);

  useEffect(() => {
    searchPeriods(filters);
  }, [filters, searchPeriods]);

  useEffect(() => {
    searchTransactionsTotal(filters);
  }, [filters, searchTransactionsTotal]);

  useEffect(() => {
    transactionServices.getCompanyExportOrder().subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  }, []);

  const formattedAccountBalance = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    Number(accountBalance ?? 0)
  );

  return (
    <Stack
      maxHeight={!isMobile ? "calc(100vh - 265px)" : undefined}
      overflow={!isMobile ? "auto" : undefined}
      width={"100%"}
    >
      {modalOpen && selectedTransaction && (
        <TransactionDetail
          transactionId={selectedTransaction.id}
          openEdit={editmodalOpen}
          handleClose={() => {
            transactionServices.getTransactionTotal(filters).subscribe({
              error: (err) => enqueueSnackbar(err.text, err.options),
            });
            transactionServices.removeTransactionById(selectedTransaction.id);
            if (selectedPeriod) {
              transactionServices.getTransactionsByPeriod(selectedPeriod, filters);
            }
            setSelectedTransaction(undefined);
            setModalOpen(false);
          }}
        />
      )}
      {exportModalOpen && (
        <ExportTransactions
          exportModalOpen={exportModalOpen}
          handleClose={() => {
            setExportModalOpen(false);
          }}
        />
      )}
      {periodModalOpen && role !== UserRole.EMPLOYEE && <ClosePeriod handleClose={() => setPeriodModalOpen(false)} />}

      <Stack p={isMobile ? 0 : 3} gap={2} width="100%" marginBottom={3}>
        <Stack pb={3} direction={isMobile ? "column" : "row"} gap={2} width="100%" justifyContent="space-between">
          <Typography fontSize="30px" fontWeight="800" style={isMobile ? { marginTop: "40px" } : undefined}>
            {t("payments.title")}
          </Typography>
          <Stack direction="row" gap={2}>
            <BasicSearchbar
              value={filters.search}
              onChange={(value) => {
                transactionServices.setFilters({
                  search: value,
                });
                setExpanded("");
                setLoadingPeriod(null);
              }}
              placeholder={t("global.search")}
            />
            {!isMobile && (
              <Button
                style={{ height: 52, width: "100%" }}
                color="secondary"
                onClick={() => setExportModalOpen(true)}
                startIcon={<img alt="" src={Export} />}
              >
                Exporter
              </Button>
            )}
          </Stack>
        </Stack>
        {role === UserRole.EMPLOYEE && user?.isDriver && company?.hasDrivers && <SharedCardSection />}
        {role !== UserRole.EMPLOYEE && (
          <Stack direction="row" gap={2} width="100%">
            <Button style={{ height: 52 }} variant="contained" onClick={() => setPeriodModalOpen(true)}>
              {t("Fermer une période")}
            </Button>
            <Stack
              sx={{
                bgcolor: Colors.green,
                color: "white",
                borderRadius: "12px",
                alignItems: "center",
                justifyContent: "center",
                p: "0 20px",
              }}
            >
              <Typography>
                <Trans i18nKey={"accountBalance"} values={{ accountBalance: formattedAccountBalance }} />
              </Typography>
            </Stack>
          </Stack>
        )}
        <Grid container direction="row" spacing={3}>
          <Grid item xs>
            <FilterCard
              color={Colors.black}
              selected={
                filters.status === undefined && filters.noProof === undefined && filters.isAmountMatch === undefined
              }
              label={t("payments.allPayments")}
              data={transactionTotal?.allTotal ?? "0"}
              onClick={() => {
                  setExpanded("")
                  transactionServices.setFilters({
                    status: undefined,
                    noProof: undefined,
                    isAmountMatch: undefined,
                    swanStatusInfo: undefined,
                  })
                }
              }
              total={transactionTotal?.allQuantity ?? 0}
            />
          </Grid>
          <Grid item xs>
            <FilterCard
              color={Colors.green}
              selected={filters.status === TransactionStatus.OK}
              label={t("OK")}
              onClick={() => {
                  setExpanded("")
                  transactionServices.setFilters({
                    status: TransactionStatus.OK,
                    noProof: undefined,
                    isAmountMatch: undefined,
                    swanStatusInfo: undefined,
                  })
                }
              }
              data={transactionTotal?.OKTotal ?? "0"}
              total={transactionTotal?.OKQuantity ?? 0}
            />
          </Grid>

          <Grid item xs>
            <FilterCard
              color={Colors.yellow}
              selected={filters.status === TransactionStatus.ACTION_REQUIRED}
              label={t("payments.toBeCompleted")}
              onClick={() => {
                  setExpanded("")
                  transactionServices.setFilters({
                    status: TransactionStatus.ACTION_REQUIRED,
                    noProof: undefined,
                    isAmountMatch: undefined,
                    swanStatusInfo: undefined,
                  })
                }
              }
              data={transactionTotal?.actionRequiredTotal ?? "0"}
              total={transactionTotal?.actionRequiredQuantity ?? 0}
            />
          </Grid>
          <Grid item xs>
            <FilterCard
              color={Colors.skyblue}
              selected={filters.swanStatusInfo === SwanStatusInfo.PENDING}
              label={t("payments.pending")}
              onClick={() => {
                  setExpanded("")
                  transactionServices.setFilters({
                    status: undefined,
                    noProof: undefined,
                    isAmountMatch: undefined,
                    swanStatusInfo: SwanStatusInfo.PENDING,
                  })
                }
              }
              data={transactionTotal?.pendingTotal ?? "0"}
              total={transactionTotal?.pendingQuantity ?? 0}
            />
          </Grid>
          {role !== UserRole.EMPLOYEE && (
            <>
              <Grid item xs>
                <FilterCard
                  color={Colors.gray}
                  selected={filters.isAmountMatch === false}
                  label={t("payments.filter.OCRFailed")}
                  data={transactionTotal?.isAmountMatchFalseTotal ?? "0"}
                  total={transactionTotal?.isAmountMatchQuantity ?? 0}
                  onClick={() =>
                    transactionServices.setFilters({
                      status: undefined,
                      noProof: undefined,
                      isAmountMatch: false,
                      swanStatusInfo: undefined,
                    })
                  }
                />
              </Grid>
              <Grid item xs>
                <FilterCard
                  color={Colors.gray}
                  selected={filters.noProof}
                  label={t("payments.filter.noJustif")}
                  data={transactionTotal?.noProofTotal ?? "0"}
                  total={transactionTotal?.noProofQuantity ?? 0}
                  onClick={() =>
                    transactionServices.setFilters({
                      status: undefined,
                      noProof: true,
                      isAmountMatch: undefined,
                      swanStatusInfo: undefined,
                    })
                  }
                />
              </Grid>
            </>
          )}
          <Grid item xs>
            <FilterCard
              color={Colors.warning}
              selected={filters.status === TransactionStatus.FAILED}
              label={t("payments.filter.refused")}
              onClick={() =>
                transactionServices.setFilters({
                  status: TransactionStatus.FAILED,
                  noProof: undefined,
                  isAmountMatch: undefined,
                })
              }
              data={transactionTotal?.failedTotal ?? ""}
              total={transactionTotal?.failedQuantity ?? 0}
            />
          </Grid>
        </Grid>
        <Stack>
          {periods?.map((period) => (
            <Accordion
              key={period.id}
              expanded={expanded === period.id}
              onChange={handleChange(period)}
            >
              <AccordionSummary
                sx={{ height: "60px !important" }}
                expandIcon={<img src={Chevron} alt="" />}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  width="100%"
                  paddingRight={1}
                >
                  <Typography
                    fontSize={isMobile ? "14px" : "16px"}
                    fontWeight="700"
                  >
                    {period.name}
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <Typography
                      fontSize={isMobile ? "14px" : "18px"}
                      fontWeight="800"
                    >
                      {Number(period.totalAmount).toLocaleString('fr-FR')}€
                    </Typography>
                    <Typography
                      fontSize={isMobile ? "14px" : "16px"}
                      fontWeight="500"
                      color={Colors.secondaryText}
                    >
                      {`(${period.transactionCount} paiements)`}
                    </Typography>
                  </Stack>
                </Stack>
              </AccordionSummary>
              {expanded === period.id && (
                <Box>
                  {loadingPeriod === period.id ? (
                    <Stack alignItems="center" py={4}>
                      <CircularProgress size={30} />
                    </Stack>
                  ) : (
                    <>
                      {transactions.map((transaction) => (
                        <AccordionDetails key={transaction.id}>
                          <PaymentCardComponent
                            transactionLight={transaction}
                            setModalOpen={setModalOpen}
                            setSelectedTransaction={setSelectedTransaction}
                          />
                        </AccordionDetails>
                      ))}
                      <Box ref={loadingRef} height={20} />
                      {isLoading && (
                        <Stack alignItems="center" py={2}>
                          <CircularProgress size={30} />
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Accordion>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PaymentScreen;
